import React from 'react'
import { Avatar, Box, Flex, Text } from '@chakra-ui/core'

export interface IAvatarStackItem {
  name: string
  src?: string
  // Solid background for a non-photo tile (e.g. a tag colour). With `icon` it backs the icon; otherwise the initials.
  bg?: string
  icon?: React.ReactNode
}

interface IProps {
  items: IAvatarStackItem[]
  // Max avatars before the rest collapse into a +N remainder.
  max?: number
  size?: number
  // RTL flips element order + overlap direction (pass from the consumer — ui has no Redux).
  isRtl?: boolean
  isLoading?: boolean
  // Render the remainder as a plain "+N" label instead of a bubble (avoids clipping large counts).
  overflowAsText?: boolean
  overflowColor?: string
  // Optional trailing node (e.g. an audience icon) rendered after the stack.
  trailing?: React.ReactNode
}

const OVERLAP = '-8px'

// Generic overlapping avatar stack: brand-neutral, no Redux, no domain resolution. Feed it resolved
// items (name + optional photo / colour / icon). Consumers (AudienceStack, AuditorsAvatars) map their
// own data into `IAvatarStackItem[]`.
export const AvatarStack: React.FC<IProps> = ({
  items,
  max = 5,
  size = 26,
  isRtl = false,
  isLoading = false,
  overflowAsText = false,
  overflowColor = '#64748b',
  trailing,
}) => {
  if (isLoading) {
    return (
      <Flex>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            w={`${size}px`}
            h={`${size}px`}
            borderRadius="full"
            bg="#eef2f7"
            border="2px solid white"
            ml={i === 0 ? '0' : OVERLAP}
          />
        ))}
      </Flex>
    )
  }

  if (!items.length) return null

  const shown = items.slice(0, max)
  const overflow = items.length - shown.length

  return (
    <Flex alignItems="center" dir="ltr" flexDirection={isRtl ? 'row-reverse' : 'row'} style={{ gap: '4px' }}>
      <Flex alignItems="center" dir="ltr" flexDirection={isRtl ? 'row-reverse' : 'row'}>
        {shown.map((item, index) => (
          <Box
            key={`${item.name}-${index}`}
            ml={index === 0 ? '0' : isRtl ? '0' : OVERLAP}
            mr={index === 0 ? '0' : isRtl ? OVERLAP : '0'}
            zIndex={shown.length - index}
            title={item.name}
            borderRadius="full"
            border="2px solid white"
            flexShrink={0}
          >
            {item.icon ? (
              <Flex
                w={`${size}px`}
                h={`${size}px`}
                borderRadius="full"
                background={item.bg}
                justifyContent="center"
                alignItems="center"
              >
                {item.icon}
              </Flex>
            ) : (
              // Only pass `bg` when explicitly provided — a bare `bg={undefined}` overrides Chakra core's
              // name-based auto colour with grey (breaks photoless user/group avatars).
              <Avatar
                src={item.src}
                name={item.name}
                w={`${size}px`}
                h={`${size}px`}
                fontSize="10px"
                {...(item.bg ? { bg: item.bg } : {})}
              />
            )}
          </Box>
        ))}
        {overflow > 0 && !overflowAsText && (
          <Flex
            ml={isRtl ? '0' : OVERLAP}
            mr={isRtl ? OVERLAP : '0'}
            w={`${size}px`}
            h={`${size}px`}
            borderRadius="full"
            bg="#e2e8f0"
            border="2px solid white"
            justifyContent="center"
            alignItems="center"
            flexShrink={0}
          >
            <Text fontSize="10px" fontWeight={700} color="#475569" dir="ltr">
              +{overflow}
            </Text>
          </Flex>
        )}
      </Flex>
      {overflow > 0 && overflowAsText && (
        <Text fontSize="11px" fontWeight={600} color={overflowColor} flexShrink={0} dir="ltr">
          +{overflow}
        </Text>
      )}
      {trailing}
    </Flex>
  )
}
